/**
 * Everything an MSAL app's src/lib/msal.ts used to set up by hand (#291): the
 * client on the shared configuration, its API scope, the token getter and
 * sign-out. Every app wired these identically, so each copy was a chance to
 * drift; now an app's msal.ts is one call.
 *
 * Structural like createAccessTokenGetter, so this package doesn't depend on
 * @azure/msal-browser: the app passes how to build its own PublicClientApplication.
 */
import { createAccessTokenGetter, type MsalTokenClient } from './msal-token';
import { createMsalConfig } from './msal-config';

type MsalAppClient<A> = MsalTokenClient<A> & { logoutRedirect(): Promise<void> };

// C is the app's own client type (kept, so callers see its full API); A, its
// account type, is inferred from it.
export function createMsalApp<C, A>(
  create: (config: ReturnType<typeof createMsalConfig>) => C & MsalAppClient<A>,
  options: { clientId: string; tenantId: string },
) {
  const msalInstance = create(createMsalConfig(options));
  /** The app API's delegated scope — the API exchanges it on-behalf-of the user. */
  const API_SCOPES = [`api://${options.clientId}/user_impersonation`];
  return {
    msalInstance,
    API_SCOPES,
    getAccessToken: createAccessTokenGetter(msalInstance, API_SCOPES),
    signOut(): void {
      void msalInstance.logoutRedirect();
    },
  };
}
